import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { withRouter, useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { TextInput } from "../../Components/Common/Form/Form";
import FormSeparator from "../../Components/Common/Form/FormSeparator/index";
import { Loader } from "../../Components/Loader/index";
import Page from "../../Components/Page/Page";
import * as actions from "../../redux/actions/projectsActions";
import * as caseActions from "../../redux/actions/caseActions";
import Table from "../../Components/Common/Table";
import IconCellRenderer from "../../Components/Common/Table/CellRenderers/IconCellRenderer";
import DefaultCellRenderer from "../../Components/Common/Table/CellRenderers/DefaultCellRenderer";
import { EditableImage } from "../../Components/Common/EditableImage/index";
import { colDefs } from "./colDefs";
import { TextArea } from "../../Components/Map/Form/TextArea";
import DeleteModal from "../../Components/Common/DeleteModal/index";
import { File } from "react-feather";
import Icon from "../../Components/Common/Icon/index";
import "./Project.scss";

const Project = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();
  const project = useSelector((state) => state.projects?.loadedProject);
  const isUpdating = useSelector((state) => state.projects?.isUpdating);

  const [deleteModal, setDeleteModal] = useState({ show: false, data: null });
  const showDeleteModal = useCallback((data) => setDeleteModal({ show: true, data }), []);
  const closeDeleteModal = useCallback(() => setDeleteModal({ show: false, data: null }), []);

  const [projectDetails, setProjectDetails] = useState({});

  useEffect(() => {
    if (project) {
      setProjectDetails({
        projectId: project.id,
        name: project.name,
        description: project.description,
        imageUrl: project.pictureDownloadUrl,
        epsg: project.epsg,
      });
    }
  }, [project]);

  const onImageChange = useCallback((image) => {
    if (!image) {
      setProjectDetails((prev) => ({ ...prev, imageUrl: null, image: null }));
      return;
    }
    const url = URL.createObjectURL(image);
    setProjectDetails((prev) => ({ ...prev, imageUrl: url, image }));
  }, []);

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setProjectDetails((prev) => ({ ...prev, [name]: value }));
  }, []);

  const submit = useCallback(() => {
    if (!isUpdating) {
      dispatch(actions.saveProject(projectDetails));
    }
  }, [isUpdating, dispatch, projectDetails]);

  const showCase = useCallback((caseDto) => {
    history.push(`/projects/${project.id}/cases/${caseDto.id}`);
  }, [history, project]);

  const createCase = useCallback(() => {
    history.push(`/projects/${project.id}/cases/create`);
  }, [history, project]);

  const goToProjectFiles = useCallback(() => {
    history.push(`/projects/${project.id}/files`);
  }, [history, project]);

  const deleteCase = useCallback((caseDto) => {
    dispatch(caseActions.deleteCase(caseDto.id));
    closeDeleteModal();
  }, [dispatch, closeDeleteModal]);

  const tableColDefs = useMemo(() => colDefs(t, showCase, showDeleteModal), [t, showCase, showDeleteModal]);

  return (
    <Page>
      <h1 style={{ fontSize: 30 }}>{t("ProjectInformation")}</h1>
      {project ? (
        <>
          <EditableImage imageUrl={projectDetails.imageUrl} onChange={onImageChange} />
          <div style={{ flexDirection: "row", display: "flex", justifyContent: "space-between" }}>
            <div className="project-heading">
              <FormSeparator label={t("Info")} />
              <TextInput label={t("Name")} name="name" value={projectDetails.name || ""} onChange={handleChange} />
              <TextInput label={t("CoordinateReferenceSystem")} name="epsg" value={`EPSG: ${project.epsg || ""}`} disabled />
              <TextArea label={t("Description")} name="description" value={projectDetails.description || ""} onChange={handleChange} />
            </div>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                marginTop: "90px",
                alignItems: "end",
              }}
            >
              <button
                className="raster-settings-btn case-sdb-btn"
                style={{ marginTop: "13px" }}
                onClick={goToProjectFiles}
              >
                <div
                  className="btn-content-wrapper btn-palm-mon-content-wrapper"
                  style={{ justifyContent: "flexStart" }}
                >
                  <div
                    className="raster-btn-lbl case-btn-lbl"
                    style={{ marginLeft: "0px", marginRight: "5px" }}
                  >
                    {t("ExternalFiles")}
                  </div>
                  <Icon
                    className={`nav-case-btn`}
                    icon={File}
                    color={"#494949"}
                    strokeWidth={"2px"}
                  />
                </div>
              </button>
            </div>
          </div>
          <div style={{ display: "flex", gap: "20px" }}>
            <button className="wizard-button wizard-button-submit" style={{ width: "110px" }} onClick={submit}>
              <span>{t("Save")}</span>
              {isUpdating && <Loader size="x-small" mode="light" />}
            </button>
          </div>
          <FormSeparator label={t("Cases")} />
          <button className="wizard-button wizard-button-submit case-button" onClick={createCase}>
            {t("NewCase")}
          </button>
          <Table colDefs={tableColDefs} rowData={project.caseDetailsDto} frameworkComponents={{ iconCell: IconCellRenderer, defaultCell: DefaultCellRenderer }} />
          <DeleteModal isOpen={deleteModal.show} modalHdr={t("DeleteCase")} closeDeleteModal={closeDeleteModal} modalInfo={t("DeleteCaseModalInfo")} deleteModalData={() => deleteModal.data ? deleteCase(deleteModal.data) : null} />
        </>
      ) : (
        <Loader />
      )}
    </Page>
  );
};

export default withRouter(Project);
